<script setup>
import { computed, ref } from 'vue';
import { format } from '@/components/base-components/components/date-picker/utils/helpers.js';
import { VIEW, MODE, SIZE, TYPE, HOURS_FORMAT } from '@/components/base-components/components/date-picker/index.js';
import { isValid, PLACEMENTS } from '@/components/base-components/utils/placement.ts';

const props = defineProps({
    value:          {
        type: [Date, Array, Object],
    },
    mode:           {
        type:      String,
        default:   MODE.SINGLE,
        validator: value => Object.values(MODE).includes(value),
    },
    view:           {
        type:      String,
        default:   VIEW.MONTH,
        validator: value => Object.values(VIEW).includes(value),
    },
    type:           {
        type:      String,
        default:   TYPE.DEFAULT,
        validator: value => Object.values(TYPE).includes(value),
    },
    size:           {
        type:      String,
        default:   SIZE.SMALL,
        validator: value => Object.values(SIZE).includes(value),
    },
    hoursFormat:    {
        type:      String,
        default:   HOURS_FORMAT.HOURS_24,
        validator: value => Object.values(HOURS_FORMAT).includes(value),
    },
    placeholder:    {
        type: String,
    },
    withTime:       {
        type: Boolean,
    },
    withSeconds:    {
        type: Boolean,
    },
    disabled:       {
        type: Boolean,
    },
    clearable:      {
        type: Boolean,
    },
    placement:      {
        type:      String,
        default:   PLACEMENTS.BOTTOM_START,
        validator: isValid,
    },
    noClickOutside: {
        type: Boolean,
    },
    noHideOnClick:  {
        type: Boolean,
    },
    render:         {
        type:      String,
        default:   'default',
        validator: value => ['default', 'lazy', 'always'].includes(value),
    },
    openDelay:      {
        type:    Number,
        default: 0,
    },
    hideDelay:      {
        type:    Number,
        default: 50,
    },
    transition:     {
        type: Object,
        default() {
            return {
                'enter-active-class': 'transition duration-150 ease-in-out origin-top scale-y-100',
                'leave-active-class': 'transition duration-150 ease-in-out origin-top scale-y-100',
                'leave-to-class':     'opacity-0 origin-top scale-y-50',
                'enter-class':        'opacity-0 origin-top scale-y-50',
            };
        },
    },
});

const emit = defineEmits({
    input: null,
});

const opened = ref(false);

function changeOpened() {
    if (props.disabled) return;

    opened.value = !opened.value;
}

const referenceHover = ref(false);

function onReferenceHover(value) {
    if (props.disabled) return;

    referenceHover.value = value;
}

const model = computed({
    get: () => {
        if (props.mode === MODE.MULTIPLE) return Array.isArray(props.value) ? props.value : [];
        if (props.mode === MODE.RANGE) return props.value ?? { start: null, end: null };

        return props.value;
    },
    set: value => emit('input', value),
});

const isModelFulfilled = computed(() => {
    if (props.mode === MODE.MULTIPLE) return model.value.length > 0;
    if (props.mode === MODE.RANGE) return !!model.value.start && !!model.value.end;

    return !!model.value;
});

const showClear = computed(() => props.clearable && referenceHover.value && isModelFulfilled.value);

function clear() {
    model.value = {
        [MODE.SINGLE]:   null,
        [MODE.MULTIPLE]: [],
        [MODE.RANGE]:    { start: null, end: null },
    }[props.mode] ?? null;
}

const formattedValue = computed(() => {
    if (!model.value) return;

    return format(model.value, props.mode, props.withTime);
});

const placeholderValue = computed(() => {
    if (props.placeholder) return props.placeholder;
    if (props.mode === MODE.MULTIPLE) return 'Pick dates...';
    if (props.mode === MODE.RANGE) return 'Pick a date range...';

    return 'Pick a date...';
});

function onHidden() {
    if (props.mode !== MODE.RANGE) return;
    if (model.value.start && model.value.end) return;

    model.value = { start: null, end: null };
}

const typeClass = computed(() => {
    if (!opened.value) return 'border-gray-400/40';

    return {
        [TYPE.DEFAULT]:       'border-science-blue-800',
        [TYPE.PRIMARY]:       'border-primary-600',
        [TYPE.SECONDARY]:     'border-secondary-950',
        [TYPE.PRIMARY_OLD]:   'border-science-blue-500',
        [TYPE.SECONDARY_OLD]: 'border-science-blue-700',
        [TYPE.SUCCESS]:       'border-emerald-600',
        [TYPE.WARNING]:       'border-orange-500',
        [TYPE.DANGER]:        'border-red-600',
        [TYPE.INFO]:          'border-gray-500',
    }[props.type];
});

function onCellClick() {
    if (!props.noHideOnClick && !props.withTime) {
        opened.value = false;
    }
}
</script>

<template>
    <base-popover
        v-model="opened"
        trigger="manual"
        tooltip-class="max-w-[calc(100vw-0.625rem)]"
        :placement="placement"
        :disabled="disabled"
        :no-click-outside="noClickOutside"
        :render="render"
        :open-delay="openDelay"
        :hide-delay="hideDelay"
        :transition="transition"
        @hidden="onHidden"
    >
        <template #reference>
            <div
                ref="referenceRef"
                class="relative flex flex-1 items-center gap-x-2 self-start overflow-hidden rounded-sm border text-gray-500 transition-colors focus:outline-hidden"
                :class="[
                    typeClass,
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer bg-white',
                    {
                        'bg-gray-500/5 opacity-60': disabled,
                        'hover:border-gray-400/80': !disabled && !opened,
                    },
                    {
                        [SIZE.SMALL]: 'min-h-[1.75rem] px-2',
                        [SIZE.MEDIUM]: 'min-h-[2rem] px-2.5',
                        [SIZE.LARGE]: 'min-h-[2.5rem] px-3',
                    }[props.size]
                ]"
                @click.stop="changeOpened"
                @mouseover="onReferenceHover(true)"
                @mouseleave="onReferenceHover(false)"
            >
                <base-icon
                    class="text-gray-300"
                    :class="[
                        {
                            [SIZE.SMALL]: 'text-xs',
                            [SIZE.MEDIUM]: 'text-sm',
                            [SIZE.LARGE]: 'text-base',
                        }[props.size]
                    ]"
                    name="calendar-days"
                />

                <base-input
                    ref="inputRef"
                    :value="formattedValue"
                    class="pointer-events-none grow"
                    wrapper-class="border-none"
                    input-class="grow truncate"
                    :placeholder="placeholderValue"
                    :size="size"
                    :disabled="disabled"
                />

                <div
                    v-if="showClear"
                    class="flex shrink-0 items-center justify-center text-gray-400/80 h-3.5 w-3.5"
                    @click.stop="clear"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke-width="1.5"
                        fill="none" stroke="currentColor"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round"
                              d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                </div>
            </div>
        </template>

        <template #content>
            <base-date-picker-content
                v-model="model"
                class="p-2"
                :mode="mode"
                :view="view"
                :type="type"
                :size="size"
                :hours-format="hoursFormat"
                :with-time="withTime"
                :with-seconds="withSeconds"
                :clearable="clearable"
                :no-hide-on-click="noHideOnClick"
                @cell-click="onCellClick"
            >
                <template #actions>
                    <base-button
                        v-if="noHideOnClick || withTime || mode !== MODE.SINGLE"
                        class="ml-auto"
                        label="Ok"
                        size="small"
                        :type="type"
                        text
                        @click="changeOpened"
                    />
                </template>
            </base-date-picker-content>
        </template>
    </base-popover>
</template>
